<script setup>
import { ContentTypeEnum, PublishedStatusEnum } from '#shared/utils/defaultDataSchema';

const props = defineProps({
  rootPath: {
    type: String,
    default: '/projects'
  },
  format: {
    type: String,
    default: ContentTypeEnum.Hub
  },
  limit: {
    type: Number,
    default: null
  },
  sort: {
    type: Array,
    default: () => ['date_modified', 'date_created', 'title']
  }
})

const queryName = props.rootPath.slice(1).replace(/\//g, "_") + (props.limit ? `-tiles_${props.limit}` : '-tiles')

const { data: hubData } = await useAsyncData(queryName, () => {

  const query = queryCollection('content')
    .where('path', 'LIKE', `${props.rootPath}%`)
    .where ('content_type', '=', props.format)
    .order('peer_order', 'ASC')
    .order('title', 'ASC')

  if(props.limit > 1 && props.limit < 50) {
    query.limit(props.limit)
  }

  if(import.meta.env.PROD) {
    query.where('published', '==', PublishedStatusEnum.Published)
  }

  return query.all()
});

const hubs = hubData?.value?.length ? [...hubData.value] : []

hubs.sort((a, b) => {
  const aOrder = Number.parseInt(a.peer_order, 10)
  const bOrder = Number.parseInt(b.peer_order, 10)
  const aSort = Number.isFinite(aOrder) ? aOrder : Number.MAX_SAFE_INTEGER
  const bSort = Number.isFinite(bOrder) ? bOrder : Number.MAX_SAFE_INTEGER

  if (aSort !== bSort) {
    return aSort - bSort
  }

  return (a.title || '').localeCompare(b.title || '')
})
</script>

<template>
  <div class="tiles-container not-prose">
    <NuxtLink
      v-for="hub in hubs"
      :key="hub.path"
      :to="hub.path"
      class="tile-all"
    >
      <div class="tile-image">
        <img 
          :alt="hub.image_alt"
          :src="hub.image"
        >
      </div>

      <div class="tile-text prose dark:prose-invert">
        <div class="tile-text-inner">
          <div class="tile-title">
            {{ hub.title }}
          </div>
          <div 
            v-if="hub.tagline"
            class="tile-caption"
          >
            {{ hub.tagline }}
          </div>
        </div>
      </div>
    </NuxtLink>
  </div>
</template>

<style lang="css" scoped>
.tiles-container {
  @apply grid gap-4
         grid-cols-1
         sm:grid-cols-2
         lg:grid-cols-3
         xl:grid-cols-4;
}

/* Entire Tile */
.tile-all {
  @apply overflow-hidden rounded-xl shadow-md
         bg-gray-100 dark:bg-gray-800
         hover:bg-gray-200 hover:dark:bg-gray-700
         transition-all duration-200;

  aspect-ratio: 1 / 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 360px;
  margin-inline: auto;
}

/* IMAGE AREA = 360x240 proportion */
.tile-image {
  flex: 0 0 66.666%;
  overflow: hidden;
}

/* Perfect image scaling */
.tile-image img {
  @apply w-full h-full object-cover;

  object-position: center;
}

/* TEXT AREA = 360x120 proportion */
.tile-text {
  flex: 0 0 33.333%;
  @apply p-4 flex items-center justify-center text-center;
}

.tile-text-inner {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
}

/* Title Styling */
.tile-title {
  @apply uppercase tracking-wide
         text-sm sm:text-base
         font-semibold leading-snug;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.tile-caption {
  flex: 1;
  font-size: 0.75rem;
  opacity: 0.8;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: right;
}

/* Zoom Effect on Hover */
.tile-image img {
  @apply w-full h-full object-cover transition-transform duration-300;
}

.tile-all:hover .tile-image img {
  transform: scale(1.05);
}

/* .tile-title {
  @apply uppercase tracking-wide text-sm sm:text-base md:text-sm lg:text-base xl:text-lg font-semibold
}
*/
/* .tile-caption {

  @apply mt-2 text-sm sm:text-base md:text-sm lg:text-base xl:text-lg text-slate-500
} */
</style>